// src/admincomponents/Table.js
"use client"

import React, { useState, useMemo } from "react";

const Table = ({ columns, data, pagination = true, itemsPerPage = 10 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  
  // --- NEW: State for tracking the selected row ---
  const [selectedRowId, setSelectedRowId] = useState(null);

  // Sorting logic
  const sortedData = useMemo(() => {
    const sortableData = [...data];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === "ascending" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const currentItems = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const cellContentStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  
  // --- NEW: Style for the highlighted row ---
  const selectedRowStyle = {
    backgroundColor: '#eaf2ff', // A light blue for highlighting
    fontWeight: 500,
  };

  return (
    <div>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", tableLayout: "fixed" }}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: column.width || 'auto' }} />
            ))}
          </colgroup>
          <thead>
            <tr style={{ background: "#f9f9f9" }}>
              {columns.map((column) => {
                // --- NEW: Style for the fixed header cell ---
                const thStyle = {
                  padding: "12px 15px",
                  textAlign: "left",
                  borderBottom: "2px solid #eee",
                  fontWeight: 600,
                  cursor: column.sortable ? "pointer" : "default",
                };
                if (column.isFixed) {
                  thStyle.position = 'sticky';
                  thStyle.left = 0;
                  thStyle.zIndex = 2; // Higher z-index for header
                  thStyle.background = '#f9f9f9'; // Ensure background is solid
                }
                
                return (
                  <th key={column.key} onClick={() => column.sortable && requestSort(column.key)} style={thStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', ...cellContentStyle }}>
                      {column.label}
                      {column.sortable && (
                        <span style={{ marginLeft: "8px", color: '#888' }}>
                          {sortConfig.key === column.key ? (sortConfig.direction === "ascending" ? "▲" : "▼") : "↕"}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((row, index) => {
                const isSelected = selectedRowId === row.id;
                
                // --- NEW: Combine base styles with conditional selected style ---
                const baseRowStyle = {
                  borderBottom: "1px solid #eee",
                  backgroundColor: index % 2 === 0 ? "white" : "#fafafa",
                  cursor: 'pointer',
                };
                
                const finalRowStyle = isSelected ? { ...baseRowStyle, ...selectedRowStyle } : baseRowStyle;

                return (
                  <tr
                    key={row.id || index}
                    style={finalRowStyle}
                    className="table-row"
                    // --- NEW: onClick handler for the entire row ---
                    onClick={() => setSelectedRowId(row.id)}
                  >
                    {columns.map((column) => {
                      const cellValue = row[column.key];

                      // --- NEW: Style for the fixed body cell ---
                      const tdStyle = {
                        padding: "12px 15px",
                        textAlign: column.align || "left",
                      };

                      if (column.isFixed) {
                        tdStyle.position = 'sticky';
                        tdStyle.left = 0;
                        tdStyle.zIndex = 1;
                        // Match the background to the final row style to prevent transparency on scroll
                        tdStyle.background = finalRowStyle.backgroundColor;
                        tdStyle.boxShadow = '2px 0 5px -2px rgba(0,0,0,0.1)';
                      }

                      return (
                        <td key={column.key} style={tdStyle}>
                          {column.render ? (
                            column.render(row)
                          ) : (
                            <div style={cellContentStyle} title={cellValue}>
                              {cellValue}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px' }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Hover style remains separate to apply over base/selected styles */}
        <style>{`
          .table-row:hover td { background-color: #f5f5f5 !important; }
          .table-row:hover td[style*="sticky"] { background-color: #f5f5f5 !important; }
        `}</style>
      </div>

      {/* Pagination remains unchanged */}
      {pagination && totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
          <div>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length} entries
          </div>
          <div style={{ display: "flex", alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 12px", margin: "0 5px", background: currentPage === 1 ? "#f5f5f5" : "white",
                border: "1px solid #ddd", borderRadius: "4px", cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <span style={{ padding: "5px 10px", fontSize: "14px", color: "#555" }}>
                Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 12px", margin: "0 5px", background: currentPage === totalPages ? "#f5f5f5" : "white",
                border: "1px solid #ddd", borderRadius: "4px", cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;